import { useCallback, useRef, useState } from 'react';
import { useApi, configApiRef, identityApiRef } from '@backstage/core-plugin-api';
import {
  deepMergeAtMatchingKeys,
  findUnreconciledEditableFields,
  isInfraDrivenCdValues,
  readCdTemplateIdentity,
  resolveEditableCdConfigView,
  EditableCdConfigMode,
} from './quickCdConfig';

export interface UseEditableCdConfigViewParams {
  repositoryName?: string;
  /** CD environment (des/int/sta/pre/pro) the config is loaded/saved for. */
  environment: string;
  onAlert: (type: 'info' | 'success' | 'error' | 'warning', message: string) => void;
}

export interface UseEditableCdConfigViewResult {
  /** 'idle' before the first load. See EditableCdConfigMode for the rest. */
  mode: EditableCdConfigMode | 'idle';
  /** Schema to render (only the fields the user had available), or null. */
  schema: Record<string, any> | null;
  /** Editable form data, matching `schema`'s shape. */
  formData: Record<string, any> | null;
  setFormData: (data: Record<string, any>) => void;
  fetchConfig: () => Promise<void>;
  saveConfig: () => Promise<void>;
  isSavingConfig: boolean;
}

/**
 * Facade around loading, editing and saving a repository's CD configuration
 * for a given environment (`GET/PUT /repo-config/:repo/cd`).
 *
 * A config created via the infra-driven flow only ever showed the user its
 * `x-quick-config` fields (see `CdConfigurationPanel`/`QuickCdConfigForm`);
 * everything else (`x-infra-output`/secrets/fixed template metadata) was
 * resolved or generated server-side. Re-opening such a config for editing
 * must therefore only show/allow editing those same fields
 * (`resolveEditableCdConfigView`, in `quickCdConfig.ts`), while still
 * preserving the hidden fields' stored values untouched on save
 * (`deepMergeAtMatchingKeys` + `findUnreconciledEditableFields` guard) — the
 * PUT endpoint is a full replace, so silently losing them would corrupt the
 * repo's real CD configuration.
 *
 * `RepoConfigEditor` only consumes this hook's return value for the CD tab;
 * it never touches the complete/raw config shape directly.
 */
export function useEditableCdConfigView({
  repositoryName,
  environment,
  onAlert,
}: UseEditableCdConfigViewParams): UseEditableCdConfigViewResult {
  const configApi = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const backendUrl = configApi.getString('backend.baseUrl');

  const [mode, setMode] = useState<EditableCdConfigMode | 'idle'>('idle');
  const [schema, setSchema] = useState<Record<string, any> | null>(null);
  const [formData, setFormData] = useState<Record<string, any> | null>(null);
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  // Complete config as last loaded from/saved to the backend, INCLUDING the
  // fields hidden from the user. Never rendered — only used to reconstruct
  // the full payload before a PUT so hidden values are never wiped out.
  const fullValuesRef = useRef<Record<string, any> | null>(null);

  const buildUrl = useCallback(() => {
    const base = `${backendUrl}/api/devops-api-proxy/repo-config/${repositoryName}/cd`;
    return environment && environment !== 'base' ? `${base}?environment=${environment}` : base;
  }, [backendUrl, repositoryName, environment]);

  const fetchConfig = useCallback(async () => {
    onAlert('info', 'Cargando configuración...');
    try {
      const token = (await identityApi.getCredentials()).token;
      const response = await fetch(buildUrl(), {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(`${data.error}`);
      }

      fullValuesRef.current = data?.values ?? null;

      // For infra-driven configs, the schema embedded in this response does
      // NOT reliably carry the `x-quick-config` keyword (see
      // resolveEditableCdConfigView's docstring): fetch the template's own
      // authoritative editor schema — the same source QuickCdConfigForm
      // trusts when the config was created — to decide what to show.
      let authoritativeEditorSchema: unknown | null = null;
      if (isInfraDrivenCdValues(data?.values)) {
        const identity = readCdTemplateIdentity(data);
        if (identity) {
          try {
            const payloadResponse = await fetch(
              `${backendUrl}/api/devops-api-proxy/template-payload/${identity.name}/${identity.version}?metadata=false`,
              {
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                },
              },
            );
            if (payloadResponse.ok) {
              const payloadData = await payloadResponse.json();
              authoritativeEditorSchema = payloadData?.editorSchema ?? null;
            }
          } catch {
            // Network error resolving the authoritative schema: fall through
            // with authoritativeEditorSchema still null, resolveEditableCdConfigView
            // degrades to 'full-fallback' rather than hiding real content.
          }
        }
      }

      const view = resolveEditableCdConfigView(data, authoritativeEditorSchema);
      setMode(view.mode);
      setSchema(view.schema);
      setFormData(view.formData);

      if (view.mode === 'no-editable-fields') {
        onAlert(
          'warning',
          'Esta configuración se creó a partir de una infraestructura y no declara ningún campo editable por el usuario.',
        );
      } else if (view.mode === 'full-fallback') {
        onAlert(
          'warning',
          'No se ha podido verificar qué campos configuraste originalmente para esta infraestructura: se muestra la configuración completa.',
        );
      } else {
        onAlert('success', 'Configuración cargada');
      }
    } catch (err) {
      if (String(err).includes('Configuration not found')) {
        fullValuesRef.current = null;
        setMode('idle');
        setSchema(null);
        setFormData(null);
        onAlert('warning', 'No existe configuración para este entorno.');
      } else {
        onAlert('error', `${err}`);
      }
    }
  }, [buildUrl, backendUrl, identityApi, onAlert]);

  const saveConfig = useCallback(async () => {
    if (isSavingConfig) return;
    setIsSavingConfig(true);
    onAlert('info', 'Guardando configuración...');
    try {
      // Reconstruct the FULL payload: the PUT endpoint is a full replace, so
      // whatever we send here becomes the entire stored config. Hidden
      // fields (x-infra-output/secrets/trazability) must survive untouched.
      const merged = deepMergeAtMatchingKeys(fullValuesRef.current, formData);
      const unreconciled = findUnreconciledEditableFields(formData, merged);
      if (unreconciled.length > 0) {
        onAlert(
          'error',
          `No se ha guardado: no se pudieron conciliar estos campos con la configuración completa (${unreconciled.join(', ')}). Contacta con la plataforma antes de reintentar.`,
        );
        return;
      }

      const token = (await identityApi.getCredentials()).token;
      const response = await fetch(buildUrl(), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(merged),
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(`${data.error}`);
      }

      fullValuesRef.current = merged;
      onAlert('success', 'Configuración guardada');
    } catch (err) {
      onAlert('error', `${err}`);
    } finally {
      setIsSavingConfig(false);
    }
  }, [isSavingConfig, formData, buildUrl, identityApi, onAlert]);

  return { mode, schema, formData, setFormData, fetchConfig, saveConfig, isSavingConfig };
}
